import { getIsCardslistLoading } from "redux/SingleDataset/selectors";
import ImageWithFallback from "views/components/ImageWithFallback";
import ScrollAwareHover from "views/components/ScrollAwareHover";
import { CardBodyImage, OnUpdateAssetData, RelevanceType } from "types";
import useHoverDelay from "helpers/utility/utilities";
import { useMemo } from "react";
import { connect } from "react-redux";
import classNames from "classnames";

import styles from "./style.module.scss";
import RelevanceTypeIcon from "views/uikit/RelevanceTypeIcon";
import { FrameIndication } from "../FrameIndication/FrameIndication";
import { isNullOrUndefined } from "../../../../helpers/utility/isNullOrUndefined";

export interface SquareImageBoxProps {
  hoverDelay?: number;
  videoURIs?: string[];
  showAssetPreview?: boolean;
  isCardslistLoading: boolean;
  plainImages: CardBodyImage[];
  thumbImages: CardBodyImage[];
  onEndContentLoading?: () => void;
  onUpdateAssetData?: OnUpdateAssetData;
}

interface SquaredImageProps {
  hoverDelay: number;
  showVideoIcon: boolean;
  showAssetPreview: boolean;
  plainImage: CardBodyImage;
  thumbImage: CardBodyImage;
  isCardslistLoading: boolean;
  relevanceType: RelevanceType | null;
  onUpdateAssetData: () => void;
  onEndContentLoading: () => void;
}

const GRID_ROWS_COLS_BY_DISPLAYED_IMAGES_COUNT: Record<
  number,
  [number, number]
> = {
  9: [3, 3],
  6: [2, 3],
  4: [2, 2],
  3: [3, 1],
  2: [2, 1],
  1: [1, 1],
};

const SquaredImage = ({
  plainImage,
  thumbImage,
  hoverDelay,
  showVideoIcon,
  relevanceType,
  showAssetPreview,
  isCardslistLoading,
  onUpdateAssetData,
  onEndContentLoading,
}: SquaredImageProps) => {
  const tableColumnClassNames = classNames(styles.tableColumn, {
    [styles.tableColumnForAssetPreview]: showAssetPreview,
  });

  const { handleMouseEnter, handleMouseLeave } = useHoverDelay(
    onUpdateAssetData,
    hoverDelay,
  );

  const handleImageLoad = () => {
    if (isCardslistLoading && onEndContentLoading) {
      onEndContentLoading();
    }
  };

  return (
    <ScrollAwareHover
      className={tableColumnClassNames}
      onMouseOver={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <>
        {!isNullOrUndefined(plainImage.timestamp) && (
          <FrameIndication timestamp={plainImage.timestamp!} />
        )}
        {relevanceType && <RelevanceTypeIcon type={relevanceType} />}
        <ImageWithFallback
          thumbImage={thumbImage}
          plainImage={plainImage}
          className={styles.singleSquareImage}
          onLoad={handleImageLoad}
        />
      </>
    </ScrollAwareHover>
  );
};

const SquareImageBox = ({
  videoURIs,
  plainImages,
  thumbImages,
  hoverDelay = 0,
  isCardslistLoading,
  showAssetPreview = true,
  onUpdateAssetData = () => {},
  onEndContentLoading = () => {},
}: SquareImageBoxProps) => {
  const displayedImagesCount = useMemo(() => {
    if (!plainImages?.length) return 0;
    if (plainImages.length >= 9) return 9;
    if (plainImages.length >= 6) return 6;
    if (plainImages.length >= 4) return 4;
    return plainImages.length;
  }, [plainImages]);

  const gridStyle = () => {
    if (displayedImagesCount) {
      const [rows, columns] =
        GRID_ROWS_COLS_BY_DISPLAYED_IMAGES_COUNT[displayedImagesCount];
      return {
        gridTemplateColumns: `repeat(${columns}, minmax(1px, 1fr))`,
        gridTemplateRows: `repeat(${rows}, minmax(1px, 1fr))`,
      };
    }
    return {};
  };

  const mergedStyle: any = {
    ...gridStyle(),
    visibility: !!displayedImagesCount ? "visible" : "hidden",
  };

  const dynamicUpdateAssetData = (index: number) => {
    const source = plainImages[index].src;
    onUpdateAssetData(plainImages[index].id, source);
  };

  return (
    <div
      className={classNames(
        styles.squareImagesContainer,
        styles[`images_${displayedImagesCount}`],
        {},
      )}
      data-testid={"square-images-container"}
      style={mergedStyle}
    >
      {plainImages?.length &&
        plainImages.map((plainImage: CardBodyImage, i: number) => {
          const thumbImage = thumbImages?.[i] || null;

          return (
            i < displayedImagesCount && (
              <SquaredImage
                key={i}
                plainImage={plainImage}
                thumbImage={thumbImage}
                hoverDelay={hoverDelay}
                showAssetPreview={showAssetPreview}
                isCardslistLoading={isCardslistLoading}
                relevanceType={plainImage.relevanceType}
                showVideoIcon={!!videoURIs?.[i] || false}
                onEndContentLoading={onEndContentLoading}
                onUpdateAssetData={() => dynamicUpdateAssetData(i)}
              />
            )
          );
        })}
    </div>
  );
};

const mapStateToProps = (state: any) => {
  return { isCardslistLoading: getIsCardslistLoading(state) };
};

export default connect(mapStateToProps)(SquareImageBox);
